import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import ProductForm from '../../components/admin/ProductForm.jsx';

const ProductsManagement = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth < 480);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [stockFilter, setStockFilter] = useState('all');
  
  const navigate = useNavigate();

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      setIsSmallScreen(window.innerWidth < 480);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Calculate available stock (stock minus sold items ONLY)
  const calculateAvailableStock = (product) => {
    return (product.inventory?.stock || 0) - (product.inventory?.soldStock || 0);
  };

  // Get stock breakdown
  const getStockBreakdown = (product) => {
    return {
      total: product.inventory?.stock || 0,
      sold: product.inventory?.soldStock || 0,
      reserved: product.inventory?.reservedStock || 0,
      available: calculateAvailableStock(product)
    };
  };

  // Calculate stock status
  const getStockStatus = (product) => {
    const stockBreakdown = getStockBreakdown(product);
    const availableStock = stockBreakdown.available;
    const threshold = product.inventory?.lowStockThreshold || 10;
    
    if (product.productType === 'pre-order' || product.productType === 'growing' || product.stockStatus === 'pre-order') {
      return { status: 'pre-order', color: 'var(--info)', text: 'Pre-Order', available: availableStock };
    }
    
    if (availableStock <= 0) return { status: 'out-of-stock', color: 'var(--error)', text: 'Out of Stock', available: availableStock };
    if (availableStock <= threshold) return { status: 'low-stock', color: 'var(--warning)', text: 'Low Stock', available: availableStock };
    return { status: 'in-stock', color: 'var(--success)', text: 'In Stock', available: availableStock };
  };

  const fetchProducts = async () => {
    try {
      setLoading(true);
      
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      const adminToken = localStorage.getItem('adminToken');
      
      const headers = {
        'Content-Type': 'application/json'
      };
      
      if (adminToken) {
        headers['Authorization'] = `Bearer ${adminToken}`;
      }
      
      const response = await fetch(`${API_URL}/admin/products?limit=100`, {
        headers: headers
      });
      
      if (response.status === 401 || response.status === 403) {
        toast.error('Admin authentication required. Please login again.');
        setProducts([]);
        return;
      }
      
      if (!response.ok) {
        throw new Error(`Server returned ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.products) {
        setProducts(data.products);
      } else {
        setProducts([]);
      }
      
    } catch (error) {
      console.error('❌ Fetch error:', error);
      
      if (error.message.includes('Failed to fetch')) {
        toast.error('Cannot connect to server. Check if backend is running.');
      } else if (error.message.includes('NetworkError')) {
        toast.error('Network error. Please check your connection.');
      } else {
        toast.error('Failed to load products.');
      }
      
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  // Filter products based on search and filters
  const filteredProducts = products.filter(product => {
    // Search filter
    const matchesSearch = searchTerm === '' || 
      product.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.sku?.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Category filter
    const matchesCategory = categoryFilter === 'all' || 
      product.category?.name?.toLowerCase() === categoryFilter.toLowerCase();
    
    // Status filter
    const matchesStatus = statusFilter === 'all' || 
      product.status?.toLowerCase() === statusFilter.toLowerCase();
    
    // Stock filter - CORRECTED LOGIC
    const stockInfo = getStockStatus(product);
    const stockBreakdown = getStockBreakdown(product);
    
    let matchesStock = true;
    if (stockFilter === 'in-stock') matchesStock = stockBreakdown.available > 0;
    if (stockFilter === 'low-stock') matchesStock = stockBreakdown.available > 0 && stockBreakdown.available <= (product.inventory?.lowStockThreshold || 10);
    if (stockFilter === 'out-of-stock') matchesStock = stockBreakdown.available <= 0;
    if (stockFilter === 'pre-order') matchesStock = product.productType === 'pre-order' || product.productType === 'growing' || product.stockStatus === 'pre-order';
    
    return matchesSearch && matchesCategory && matchesStatus && matchesStock;
  });

  // Get unique categories
  const categories = [...new Set(products.map(p => p.category?.name).filter(Boolean))];

  // Delete product
  const handleDeleteProduct = async (productId) => {
    if (window.confirm('Are you sure you want to delete this product? This action cannot be undone.')) {
      try {
        const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
        const adminToken = localStorage.getItem('adminToken');
        
        const response = await fetch(`${API_URL}/admin/products/${productId}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${adminToken}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (response.ok) {
          toast.success('Product deleted successfully');
          fetchProducts();
        } else {
          throw new Error('Failed to delete product');
        }
      } catch (error) {
        console.error('Error deleting product:', error);
        toast.error('Failed to delete product');
      }
    }
  };

  // Edit product
  const handleEditProduct = (product) => {
    setEditingProduct(product);
    setShowForm(true);
  };

  // Add new product
  const handleAddProduct = () => {
    setEditingProduct(null);
    setShowForm(true);
  };

  // View product details
  const handleViewProduct = (productId) => {
  // Navigate to product detail with proper state
  navigate(`/admin/products/${productId}`, {
    state: { fromProductsPage: true }
  });
};

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <div className="admin-content" style={{ 
      padding: isSmallScreen ? 'var(--space-2)' : (isMobile ? 'var(--space-4)' : 'var(--space-6)'),
      maxWidth: '100%',
      overflowX: 'hidden'
    }}>
      {/* Header */}
      <div style={{ 
        marginBottom: isSmallScreen ? 'var(--space-3)' : (isMobile ? 'var(--space-5)' : 'var(--space-6)'),
        paddingBottom: isSmallScreen ? 'var(--space-2)' : (isMobile ? 'var(--space-3)' : 'var(--space-4)'),
        borderBottom: '1px solid var(--border-light)'
      }}>
        <div style={{ 
          display: 'flex', 
          flexDirection: isSmallScreen ? 'column' : 'row',
          alignItems: isSmallScreen ? 'flex-start' : 'center',
          justifyContent: 'space-between',
          gap: isSmallScreen ? 'var(--space-2)' : 'var(--space-4)',
          marginBottom: isSmallScreen ? 'var(--space-2)' : 'var(--space-3)'
        }}>
          <div>
            <h1 className="accent-gold" style={{ 
              fontSize: isSmallScreen ? 'var(--font-size-lg)' : (isMobile ? 'var(--font-size-xl)' : 'var(--font-size-2xl)'),
              marginBottom: isSmallScreen ? 'var(--space-1)' : 'var(--space-2)'
            }}>
              Products Management
            </h1>
            <p style={{ 
              color: 'var(--text-medium)',
              fontSize: isSmallScreen ? 'var(--font-size-xs)' : (isMobile ? 'var(--font-size-sm)' : 'var(--font-size-base)')
            }}>
              Manage your product catalog and inventory
            </p>
          </div>
          <button 
            className="btn btn-gold"
            onClick={handleAddProduct}
            style={{ 
              padding: isSmallScreen ? '8px 12px' : (isMobile ? 'var(--space-2) var(--space-3)' : 'var(--space-3) var(--space-4)'),
              fontSize: isSmallScreen ? 'var(--font-size-xs)' : (isMobile ? 'var(--font-size-sm)' : 'var(--font-size-base)'),
              whiteSpace: 'nowrap'
            }}
          >
            <span style={{ marginRight: 'var(--space-1)' }}>+</span>
            Add Product
          </button>
        </div>
        
        {/* Stats Bar */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: isSmallScreen ? 'repeat(2, 1fr)' : (isMobile ? 'repeat(4, 1fr)' : 'repeat(5, 1fr)'),
          gap: isSmallScreen ? '6px' : (isMobile ? 'var(--space-2)' : 'var(--space-3)'),
          marginTop: isSmallScreen ? 'var(--space-2)' : 'var(--space-3)'
        }}>
          {[
            { label: 'Total', value: products.length, color: 'var(--primary)', icon: '📦' },
            { label: 'Active', value: products.filter(p => p.status === 'active').length, color: 'var(--success)', icon: '✅' },
            { label: 'Inactive', value: products.filter(p => p.status === 'inactive').length, color: 'var(--warning)', icon: '⏸️' },
            { label: 'Out of Stock', value: products.filter(p => {
              const stockBreakdown = getStockBreakdown(p);
              return stockBreakdown.available <= 0;
            }).length, color: 'var(--error)', icon: '⚠️' },
            { label: 'Low Stock', value: products.filter(p => {
              const stockBreakdown = getStockBreakdown(p);
              const threshold = p.inventory?.lowStockThreshold || 10;
              return stockBreakdown.available > 0 && stockBreakdown.available <= threshold;
            }).length, color: 'var(--warning)', icon: '📉' }
          ].map((stat, index) => (
            <div key={index} style={{ 
              textAlign: 'center',
              padding: isSmallScreen ? '6px' : (isMobile ? 'var(--space-2)' : 'var(--space-3)'),
              backgroundColor: 'var(--bg-light)',
              borderRadius: 'var(--radius-md)',
              border: `1px solid ${stat.color}20`
            }}>
              <div style={{ 
                fontSize: isSmallScreen ? 'var(--font-size-lg)' : (isMobile ? 'var(--font-size-xl)' : 'var(--font-size-2xl)'), 
                fontWeight: '700', 
                color: stat.color,
                marginBottom: '2px'
              }}>
                {stat.value}
              </div>
              <div style={{ 
                fontSize: isSmallScreen ? '9px' : (isMobile ? 'var(--font-size-xs)' : 'var(--font-size-sm)'), 
                color: 'var(--text-medium)',
                whiteSpace: 'nowrap'
              }}>
                {stat.icon} {stat.label}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Filters & Search */}
      <div className="card" style={{ 
        marginBottom: isSmallScreen ? 'var(--space-2)' : (isMobile ? 'var(--space-4)' : 'var(--space-5)'),
        padding: isSmallScreen ? 'var(--space-2)' : (isMobile ? 'var(--space-3)' : 'var(--space-4)')
      }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: isSmallScreen ? '1fr' : (isMobile ? '1fr 1fr' : '2fr repeat(3, 1fr)'),
          gap: isSmallScreen ? 'var(--space-2)' : (isMobile ? 'var(--space-2)' : 'var(--space-3)'),
          alignItems: 'end'
        }}>
          {/* Search */}
          <div>
            <label style={{ 
              display: 'block', 
              fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)',
              marginBottom: '4px',
              color: 'var(--text-medium)',
              fontWeight: '500'
            }}>
              Search Products
            </label>
            <input
              type="text"
              placeholder="Search by name, description, SKU..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: '100%',
                padding: isSmallScreen ? '8px 12px' : (isMobile ? 'var(--space-2)' : 'var(--space-3)'),
                border: '1px solid var(--border-light)',
                borderRadius: 'var(--radius-md)',
                fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)',
                backgroundColor: 'var(--bg-white)',
                color: 'var(--text-dark)'
              }}
            />
          </div>
          
          {/* Category Filter */}
          <div>
            <label style={{ 
              display: 'block', 
              fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)',
              marginBottom: '4px',
              color: 'var(--text-medium)',
              fontWeight: '500'
            }}>
              Category
            </label>
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              style={{
                width: '100%',
                padding: isSmallScreen ? '8px 12px' : (isMobile ? 'var(--space-2)' : 'var(--space-3)'),
                border: '1px solid var(--border-light)',
                borderRadius: 'var(--radius-md)',
                fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)',
                backgroundColor: 'var(--bg-white)',
                color: 'var(--text-dark)',
                cursor: 'pointer'
              }}
            >
              <option value="all">All Categories</option>
              {categories.map((category, index) => (
                <option key={index} value={category}>{category}</option>
              ))}
            </select>
          </div>
          
          {/* Status Filter */}
          <div>
            <label style={{ 
              display: 'block', 
              fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)',
              marginBottom: '4px',
              color: 'var(--text-medium)',
              fontWeight: '500'
            }}>
              Status
            </label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              style={{
                width: '100%',
                padding: isSmallScreen ? '8px 12px' : (isMobile ? 'var(--space-2)' : 'var(--space-3)'),
                border: '1px solid var(--border-light)',
                borderRadius: 'var(--radius-md)',
                fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)',
                backgroundColor: 'var(--bg-white)',
                color: 'var(--text-dark)',
                cursor: 'pointer'
              }}
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="draft">Draft</option>
            </select>
          </div>
          
          {/* Stock Filter */}
          <div>
            <label style={{ 
              display: 'block', 
              fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)',
              marginBottom: '4px',
              color: 'var(--text-medium)',
              fontWeight: '500'
            }}>
              Stock
            </label>
            <select
              value={stockFilter}
              onChange={(e) => setStockFilter(e.target.value)}
              style={{
                width: '100%',
                padding: isSmallScreen ? '8px 12px' : (isMobile ? 'var(--space-2)' : 'var(--space-3)'),
                border: '1px solid var(--border-light)',
                borderRadius: 'var(--radius-md)',
                fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)',
                backgroundColor: 'var(--bg-white)',
                color: 'var(--text-dark)',
                cursor: 'pointer'
              }}
            >
              <option value="all">All Stock</option>
              <option value="in-stock">In Stock</option>
              <option value="low-stock">Low Stock</option>
              <option value="out-of-stock">Out of Stock</option>
              <option value="pre-order">Pre-Order</option>
            </select>
          </div>
        </div>
      </div>

      {/* Products Card */}
      <div className="card card-gold">
        <div className="card-header" style={{
          padding: isSmallScreen ? 'var(--space-2) var(--space-3)' : (isMobile ? 'var(--space-3) var(--space-4)' : 'var(--space-4) var(--space-5)'),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: isSmallScreen ? 'var(--space-1)' : 'var(--space-2)'
        }}>
          <h3 style={{ 
            margin: 0,
            fontSize: isSmallScreen ? 'var(--font-size-sm)' : (isMobile ? 'var(--font-size-base)' : 'var(--font-size-lg)'),
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-2)'
          }}>
            <span style={{ color: 'var(--accent-gold)' }}>🛍️</span>
            Products ({filteredProducts.length} of {products.length})
          </h3>
          
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: isSmallScreen ? '4px' : 'var(--space-2)',
            flexWrap: 'wrap'
          }}>
            <button 
              onClick={fetchProducts}
              className="btn btn-secondary"
              disabled={loading}
              style={{ 
                padding: isSmallScreen ? '4px 6px' : (isMobile ? 'var(--space-1) var(--space-2)' : 'var(--space-2) var(--space-3)'),
                fontSize: isSmallScreen ? '10px' : 'var(--font-size-xs)',
                whiteSpace: 'nowrap'
              }}
            >
              {loading ? '🔄' : '↻ Refresh'}
            </button>
            
            {!isSmallScreen && (
              <span style={{ 
                fontSize: 'var(--font-size-xs)', 
                color: 'var(--text-light)',
                whiteSpace: 'nowrap'
              }}>
                Last updated: {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </span>
            )}
          </div>
        </div>
        
        <div className="card-body" style={{
          padding: isSmallScreen ? 'var(--space-2)' : (isMobile ? 'var(--space-3)' : 'var(--space-4)')
        }}>
          {loading ? (
            <div style={{ 
              textAlign: 'center', 
              padding: 'var(--space-8)', 
              color: 'var(--text-light)'
            }}>
              <div style={{ marginBottom: 'var(--space-2)' }}>🔄</div>
              Loading products...
            </div>
          ) : filteredProducts.length === 0 ? (
            <div style={{ 
              textAlign: 'center', 
              padding: isSmallScreen ? 'var(--space-6)' : 'var(--space-10)', 
              color: 'var(--text-light)'
            }}>
              <div style={{ fontSize: '3rem', marginBottom: 'var(--space-3)', opacity: 0.3 }}>📦</div>
              <p style={{ 
                fontWeight: '600', 
                color: 'var(--text-dark)',
                fontSize: isSmallScreen ? 'var(--font-size-sm)' : 'var(--font-size-base)',
                marginBottom: 'var(--space-2)'
              }}>
                No products found
              </p>
              <p style={{ 
                fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)', 
                marginBottom: 'var(--space-4)',
                maxWidth: '300px',
                margin: '0 auto'
              }}>
                {searchTerm || categoryFilter !== 'all' || statusFilter !== 'all' || stockFilter !== 'all' 
                  ? 'Try adjusting your filters or search terms.' 
                  : 'Add your first product to get started.'}
              </p>
              <button 
                className="btn btn-gold"
                onClick={handleAddProduct}
                style={{ 
                  padding: isSmallScreen ? '8px 16px' : 'var(--space-3) var(--space-5)',
                  fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)'
                }}
              >
                + Add New Product
              </button>
            </div>
          ) : isSmallScreen ? (
            // Mobile Cards View
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {filteredProducts.map((product) => {
                const stockInfo = getStockStatus(product);
                const stockBreakdown = getStockBreakdown(product);
                
                return (
                  <div key={product._id} style={{
                    padding: '12px',
                    backgroundColor: 'var(--bg-light)',
                    borderRadius: 'var(--radius-md)',
                    border: '1px solid var(--border-light)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px'
                  }}>
                    {/* Product Header */}
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'flex-start',
                      justifyContent: 'space-between',
                      gap: '8px'
                    }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ 
                          fontWeight: '700', 
                          color: 'var(--text-dark)',
                          fontSize: 'var(--font-size-xs)',
                          marginBottom: '2px',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}>
                          {product.name || 'Unnamed Product'}
                        </div>
                        <div style={{ 
                          fontSize: '9px', 
                          color: 'var(--text-light)'
                        }}>
                          {product.category?.name || 'No category'} • {product.sku || 'No SKU'}
                        </div>
                      </div>
                      
                      <div style={{ 
                        display: 'flex', 
                        flexDirection: 'column',
                        alignItems: 'flex-end',
                        gap: '2px'
                      }}>
                        <span style={{ 
                          backgroundColor: stockInfo.color,
                          color: 'white',
                          padding: '2px 6px',
                          borderRadius: 'var(--radius-sm)',
                          fontSize: '8px',
                          fontWeight: '600',
                          whiteSpace: 'nowrap'
                        }}>
                          {stockInfo.text}
                        </span>
                        <div style={{ 
                          fontSize: '10px', 
                          fontWeight: '700', 
                          color: 'var(--accent-gold)'
                        }}>
                          KSh {product.price?.toLocaleString() || '0'}
                        </div>
                      </div>
                    </div>
                    
                    {/* Product Image & Details */}
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center',
                      gap: '10px'
                    }}>
                      {product.featuredImage?.url ? (
                        <img 
                          src={product.featuredImage.url} 
                          alt={product.featuredImage.alt || product.name}
                          style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: 'var(--radius-md)',
                            objectFit: 'cover',
                            flexShrink: 0
                          }}
                        />
                      ) : (
                        <div style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: 'var(--radius-md)',
                          backgroundColor: 'var(--bg-gray)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'var(--text-light)',
                          flexShrink: 0
                        }}>
                          🖼️
                        </div>
                      )}
                      
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ 
                          fontSize: '9px', 
                          color: 'var(--text-light)',
                          marginBottom: '2px',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '2px'
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span>Available: <strong>{stockBreakdown.available}</strong></span>
                            <span>Total: <strong>{stockBreakdown.total}</strong></span>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span>Sold: <strong>{stockBreakdown.sold}</strong></span>
                            <span>Reserved: <strong>{stockBreakdown.reserved}</strong></span>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span>Status: <strong style={{ 
                              color: product.status === 'active' ? 'var(--success)' : 'var(--error)'
                            }}>{product.status}</strong></span>
                          </div>
                        </div>
                        
                        <div style={{ 
                          fontSize: '8px', 
                          color: 'var(--text-light)'
                        }}>
                          Updated: {formatDate(product.updatedAt)}
                        </div>
                      </div>
                    </div>
                    
                    {/* Actions */}
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between',
                      gap: '4px',
                      marginTop: '4px'
                    }}>
                      <button 
                        onClick={() => handleViewProduct(product._id)}
                        className="btn btn-secondary"
                        style={{ 
                          flex: 1,
                          padding: '4px 6px',
                          fontSize: '9px'
                        }}
                      >
                        View
                      </button>
                      <button 
                        onClick={() => handleEditProduct(product)}
                        className="btn btn-secondary"
                        style={{ 
                          flex: 1,
                          padding: '4px 6px',
                          fontSize: '9px'
                        }}
                      >
                        Edit
                      </button>
                      <button 
                        onClick={() => handleDeleteProduct(product._id)}
                        className="btn btn-danger"
                        style={{ 
                          flex: 1,
                          padding: '4px 6px',
                          fontSize: '9px'
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : isMobile ? (
            // Tablet Cards View
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
              {filteredProducts.map((product) => {
                const stockInfo = getStockStatus(product);
                const stockBreakdown = getStockBreakdown(product);
                
                return (
                  <div key={product._id} style={{
                    padding: 'var(--space-3)',
                    backgroundColor: 'var(--bg-light)',
                    borderRadius: 'var(--radius-md)',
                    border: '1px solid var(--border-light)',
                    display: 'grid',
                    gridTemplateColumns: 'auto 1fr auto',
                    gap: 'var(--space-3)',
                    alignItems: 'center'
                  }}>
                    {/* Image */}
                    {product.featuredImage?.url ? (
                      <img 
                        src={product.featuredImage.url} 
                        alt={product.featuredImage.alt || product.name}
                        style={{
                          width: '50px',
                          height: '50px',
                          borderRadius: 'var(--radius-md)',
                          objectFit: 'cover'
                        }}
                      />
                    ) : (
                      <div style={{
                        width: '50px',
                        height: '50px',
                        borderRadius: 'var(--radius-md)',
                        backgroundColor: 'var(--bg-gray)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'var(--text-light)'
                      }}>
                        🖼️
                      </div>
                    )}
                    
                    {/* Details */}
                    <div style={{ minWidth: 0 }}>
                      <div style={{ 
                        fontWeight: '700', 
                        color: 'var(--text-dark)',
                        fontSize: 'var(--font-size-sm)',
                        marginBottom: '2px',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}>
                        {product.name || 'Unnamed Product'}
                      </div>
                      <div style={{ 
                        fontSize: 'var(--font-size-xs)', 
                        color: 'var(--text-light)',
                        marginBottom: '4px'
                      }}>
                        {product.category?.name || 'No category'} • {product.sku || 'No SKU'}
                      </div>
                      <div style={{ 
                        display: 'flex', 
                        flexDirection: 'column',
                        gap: '2px',
                        fontSize: 'var(--font-size-xs)'
                      }}>
                        <div style={{ display: 'flex', gap: '12px' }}>
                          <span>Available: <strong>{stockBreakdown.available}</strong></span>
                          <span>Total: <strong>{stockBreakdown.total}</strong></span>
                          <span>Sold: <strong>{stockBreakdown.sold}</strong></span>
                          <span>Reserved: <strong>{stockBreakdown.reserved}</strong></span>
                        </div>
                        <div style={{ display: 'flex', gap: '12px' }}>
                          <span>
                            Status: <strong style={{ 
                              color: product.status === 'active' ? 'var(--success)' : 'var(--error)'
                            }}>{product.status}</strong>
                          </span>
                          <span>
                            Price: <strong style={{ color: 'var(--accent-gold)' }}>
                              KSh {product.price?.toLocaleString() || '0'}
                            </strong>
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Actions & Status */}
                    <div style={{ 
                      display: 'flex', 
                      flexDirection: 'column',
                      alignItems: 'flex-end',
                      gap: 'var(--space-2)'
                    }}>
                      <span style={{ 
                        backgroundColor: stockInfo.color,
                        color: 'white',
                        padding: '2px 8px',
                        borderRadius: 'var(--radius-sm)',
                        fontSize: 'var(--font-size-xs)',
                        fontWeight: '600',
                        whiteSpace: 'nowrap'
                      }}>
                        {stockInfo.text}
                      </span>
                      
                      <div style={{ 
                        display: 'flex', 
                        gap: '4px'
                      }}>
                        <button 
                          onClick={() => handleViewProduct(product._id)}
                          className="btn btn-secondary"
                          style={{ 
                            padding: '4px 8px',
                            fontSize: 'var(--font-size-xs)'
                          }}
                        >
                          View
                        </button>
                        <button 
                          onClick={() => handleEditProduct(product)}
                          className="btn btn-secondary"
                          style={{ 
                            padding: '4px 8px',
                            fontSize: 'var(--font-size-xs)'
                          }}
                        >
                          Edit
                        </button>
                        <button 
                          onClick={() => handleDeleteProduct(product._id)}
                          className="btn btn-danger"
                          style={{ 
                            padding: '4px 8px',
                            fontSize: 'var(--font-size-xs)'
                          }}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            // Desktop Table View
            <div style={{ 
              overflowX: 'auto',
              borderRadius: 'var(--radius-md)',
              border: '1px solid var(--border-light)'
            }}>
              <table style={{ 
                width: '100%', 
                borderCollapse: 'collapse',
                minWidth: '1000px'
              }}>
                <thead>
                  <tr style={{ 
                    borderBottom: '2px solid var(--border-light)',
                    backgroundColor: 'var(--bg-light)'
                  }}>
                    <th style={{ 
                      padding: 'var(--space-3) var(--space-4)', 
                      textAlign: 'left', 
                      color: 'var(--text-medium)',
                      fontWeight: '600',
                      fontSize: 'var(--font-size-sm)',
                      whiteSpace: 'nowrap'
                    }}>Product</th>
                    <th style={{ 
                      padding: 'var(--space-3) var(--space-4)', 
                      textAlign: 'left', 
                      color: 'var(--text-medium)',
                      fontWeight: '600',
                      fontSize: 'var(--font-size-sm)',
                      whiteSpace: 'nowrap'
                    }}>SKU</th>
                    <th style={{ 
                      padding: 'var(--space-3) var(--space-4)', 
                      textAlign: 'left', 
                      color: 'var(--text-medium)',
                      fontWeight: '600',
                      fontSize: 'var(--font-size-sm)',
                      whiteSpace: 'nowrap'
                    }}>Category</th>
                    <th style={{ 
                      padding: 'var(--space-3) var(--space-4)', 
                      textAlign: 'left', 
                      color: 'var(--text-medium)',
                      fontWeight: '600',
                      fontSize: 'var(--font-size-sm)',
                      whiteSpace: 'nowrap'
                    }}>Price</th>
                    <th style={{ 
                      padding: 'var(--space-3) var(--space-4)', 
                      textAlign: 'left', 
                      color: 'var(--text-medium)',
                      fontWeight: '600',
                      fontSize: 'var(--font-size-sm)',
                      whiteSpace: 'nowrap'
                    }}>Stock Details</th>
                    <th style={{ 
                      padding: 'var(--space-3) var(--space-4)', 
                      textAlign: 'left', 
                      color: 'var(--text-medium)',
                      fontWeight: '600',
                      fontSize: 'var(--font-size-sm)',
                      whiteSpace: 'nowrap'
                    }}>Status</th>
                    <th style={{ 
                      padding: 'var(--space-3) var(--space-4)', 
                      textAlign: 'left', 
                      color: 'var(--text-medium)',
                      fontWeight: '600',
                      fontSize: 'var(--font-size-sm)',
                      whiteSpace: 'nowrap'
                    }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredProducts.map((product) => {
                    const stockInfo = getStockStatus(product);
                    const stockBreakdown = getStockBreakdown(product);
                    
                    return (
                      <tr key={product._id} style={{ 
                        borderBottom: '1px solid var(--border-light)',
                        transition: 'background-color 0.2s ease'
                      }}>
                        <td style={{ padding: 'var(--space-3) var(--space-4)' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
                            {product.featuredImage?.url ? (
                              <img 
                                src={product.featuredImage.url} 
                                alt={product.featuredImage.alt || product.name}
                                style={{
                                  width: '40px',
                                  height: '40px',
                                  borderRadius: 'var(--radius-md)',
                                  objectFit: 'cover'
                                }}
                              />
                            ) : (
                              <div style={{
                                width: '40px',
                                height: '40px',
                                borderRadius: 'var(--radius-md)',
                                backgroundColor: 'var(--bg-gray)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'var(--text-light)'
                              }}>
                                🖼️
                              </div>
                            )}
                            <div style={{ minWidth: 0 }}>
                              <div style={{ 
                                fontWeight: '600', 
                                color: 'var(--text-dark)',
                                fontSize: 'var(--font-size-sm)',
                                marginBottom: '2px',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                maxWidth: '200px'
                              }}>
                                {product.name}
                              </div>
                              <div style={{ 
                                fontSize: 'var(--font-size-xs)', 
                                color: 'var(--text-light)'
                              }}>
                                {product.productType || 'Standard'}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td style={{ 
                          padding: 'var(--space-3) var(--space-4)',
                          fontSize: 'var(--font-size-sm)',
                          fontFamily: 'monospace'
                        }}>
                          {product.sku || 'N/A'}
                        </td>
                        <td style={{ 
                          padding: 'var(--space-3) var(--space-4)',
                          fontSize: 'var(--font-size-sm)'
                        }}>
                          <span style={{ 
                            backgroundColor: 'var(--bg-light)',
                            color: 'var(--text-medium)',
                            padding: '4px 8px',
                            borderRadius: 'var(--radius-sm)',
                            fontSize: 'var(--font-size-xs)',
                            fontWeight: '500'
                          }}>
                            {product.category?.name || 'Uncategorized'}
                          </span>
                        </td>
                        <td style={{ 
                          padding: 'var(--space-3) var(--space-4)',
                          fontSize: 'var(--font-size-sm)',
                          fontWeight: '600',
                          color: 'var(--accent-gold)'
                        }}>
                          KSh {product.price?.toLocaleString() || '0'}
                        </td>
                        <td style={{ padding: 'var(--space-3) var(--space-4)' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                            <div style={{ 
                              fontWeight: '600',
                              fontSize: 'var(--font-size-sm)',
                              color: 'var(--text-dark)'
                            }}>
                              {stockBreakdown.available}
                            </div>
                            <span style={{ 
                              backgroundColor: stockInfo.color,
                              color: 'white',
                              padding: '2px 8px',
                              borderRadius: 'var(--radius-sm)',
                              fontSize: 'var(--font-size-xs)',
                              fontWeight: '600'
                            }}>
                              {stockInfo.text}
                            </span>
                          </div>
                          <div style={{ 
                            fontSize: '10px', 
                            color: 'var(--text-light)',
                            marginTop: '4px',
                            display: 'flex',
                            gap: '8px'
                          }}>
                            <span>Total: {stockBreakdown.total}</span>
                            <span>Sold: {stockBreakdown.sold}</span>
                            <span>Reserved: {stockBreakdown.reserved}</span>
                          </div>
                        </td>
                        <td style={{ padding: 'var(--space-3) var(--space-4)' }}>
                          <span style={{ 
                            backgroundColor: product.status === 'active' ? 'var(--success)' : 'var(--error)',
                            color: 'white',
                            padding: '4px 8px',
                            borderRadius: 'var(--radius-sm)',
                            fontSize: 'var(--font-size-xs)',
                            fontWeight: '600',
                            textTransform: 'capitalize'
                          }}>
                            {product.status}
                          </span>
                        </td>
                        <td style={{ padding: 'var(--space-3) var(--space-4)' }}>
                          <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                            <button 
                              onClick={() => handleViewProduct(product._id)}
                              className="btn btn-secondary"
                              style={{ 
                                padding: 'var(--space-2) var(--space-3)',
                                fontSize: 'var(--font-size-xs)'
                              }}
                            >
                              View
                            </button>
                            <button 
                              onClick={() => handleEditProduct(product)}
                              className="btn btn-secondary"
                              style={{ 
                                padding: 'var(--space-2) var(--space-3)',
                                fontSize: 'var(--font-size-xs)'
                              }}
                            >
                              Edit
                            </button>
                            <button 
                              onClick={() => handleDeleteProduct(product._id)}
                              className="btn btn-danger"
                              style={{ 
                                padding: 'var(--space-2) var(--space-3)',
                                fontSize: 'var(--font-size-xs)'
                              }}
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
        
        {/* Pagination/Footer */}
        {filteredProducts.length > 0 && !loading && (
          <div style={{ 
            padding: isSmallScreen ? 'var(--space-2) var(--space-3)' : (isMobile ? 'var(--space-3) var(--space-4)' : 'var(--space-4) var(--space-5)'),
            borderTop: '1px solid var(--border-light)',
            backgroundColor: 'var(--bg-light)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: isSmallScreen ? 'var(--space-1)' : 'var(--space-2)'
          }}>
            <div style={{ 
              fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)',
              color: 'var(--text-medium)'
            }}>
              Showing {filteredProducts.length} of {products.length} products
            </div>
            
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: isSmallScreen ? '4px' : 'var(--space-2)'
            }}>
              <button 
                className="btn btn-secondary"
                onClick={fetchProducts}
                style={{ 
                  padding: isSmallScreen ? '4px 8px' : (isMobile ? 'var(--space-2)' : 'var(--space-3) var(--space-4)'),
                  fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)'
                }}
              >
                Refresh List
              </button>
              
              <button 
                className="btn btn-gold"
                onClick={handleAddProduct}
                style={{ 
                  padding: isSmallScreen ? '4px 8px' : (isMobile ? 'var(--space-2)' : 'var(--space-3) var(--space-4)'),
                  fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)'
                }}
              >
                + Add New Product
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Product Form Modal */}
      {showForm && (
        <ProductForm 
          product={editingProduct}
          onClose={() => {
            setShowForm(false);
            setEditingProduct(null);
          }}
          onSave={() => {
            setShowForm(false);
            setEditingProduct(null);
            fetchProducts();
          }}
        />
      )}
    </div>
  );
};

export default ProductsManagement;